/**
 * Image renderer (Library / file-preview).
 *
 * Renders a `kind: "image"` artifact (or any image URL source) through the
 * exact same {@link ImageArtifact} the chat artifact canvas uses — a
 * centered image on a checker backdrop so transparent PNGs read correctly —
 * so opening an image from the Library matches the chat canvas.
 *
 * The source adapter resolves the URL (a direct URL for attachments /
 * tool-output images, or the parsed URL from an `image` artifact body); this
 * renderer just hands the resulting blob URL to the shared viewer.
 */
import { Loader2 } from "lucide-react";

import { ImageArtifact } from "@/components/artifacts/image-artifact";

import { useFileBlobUrl } from "../hooks/use-file-blob-url";
import type { FileRendererProps } from "../types";

export function ImageRenderer({ source }: FileRendererProps) {
  const state = useFileBlobUrl(source, { inline: true });

  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <p>Unable to display this image.</p>
        <p className="text-xs opacity-70">{state.error.message}</p>
      </div>
    );
  }

  return (
    <ImageArtifact url={state.url} alt={source.filename} className="h-full w-full" />
  );
}
